import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal, X, PackageSearch } from "lucide-react";
import { BROWSE } from "../lib/testIds";
import { fetchItems, fetchItemsMeta } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ItemCard from "../components/ItemCard";
import ItemDetailModal from "../components/ItemDetailModal";

const useDebounced = (value, delay = 300) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const Browse = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("all");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [onlyAvailable, setOnlyAvailable] = useState(false);

    const [activeItem, setActiveItem] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const debouncedSearch = useDebounced(search, 350);
    const debouncedMin = useDebounced(minPrice, 350);
    const debouncedMax = useDebounced(maxPrice, 350);

    useEffect(() => {
        fetchItemsMeta()
            .then((m) => setCategories(m.categories || []))
            .catch(() => setCategories([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchItems({
            q: debouncedSearch || undefined,
            category,
            min_price: debouncedMin !== "" ? Number(debouncedMin) : undefined,
            max_price: debouncedMax !== "" ? Number(debouncedMax) : undefined,
            available: onlyAvailable ? true : undefined,
        })
            .then((data) => {
                if (cancelled) return;
                // Hide the caller's own listings — you can't rent your own
                // gear and it just clutters the browse grid.
                const filtered = user?.user_id
                    ? (data || []).filter((it) => it.owner_id !== user.user_id)
                    : data || [];
                setItems(filtered);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedSearch, category, debouncedMin, debouncedMax, onlyAvailable, user?.user_id]);

    const hasFilters = useMemo(
        () =>
            !!search ||
            category !== "all" ||
            minPrice !== "" ||
            maxPrice !== "" ||
            onlyAvailable,
        [search, category, minPrice, maxPrice, onlyAvailable],
    );

    const clearFilters = () => {
        setSearch("");
        setCategory("all");
        setMinPrice("");
        setMaxPrice("");
        setOnlyAvailable(false);
    };

    const openItem = (item) => {
        setActiveItem(item);
        setModalOpen(true);
    };

    return (
        <main
            data-testid={BROWSE.root}
            className="relative z-10 mx-auto max-w-7xl px-6 py-14 md:px-10 md:py-20"
        >
            {/* Header */}
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-[#F4F1EA] px-3 py-1.5">
                <PackageSearch className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                    BROWSE EASTON PARK
                </span>
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-[#2e3220] sm:text-5xl">
                Browse items from your Lendly Neighbors
            </h1>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#5a6142]">
                Tools, gear, and gadgets from neighbors near you. Pay a fair daily price —
                no shipping, no warehouses, no buying things you&apos;ll use twice.
            </p>

            {/* Filters */}
            <div className="mt-8 rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4 shadow-sm md:p-5">
                <div className="grid gap-3 md:grid-cols-[1.6fr_1fr_auto_auto] md:items-end">
                    {/* Search */}
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Search
                        </span>
                        <div className="relative mt-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#737a3d]" />
                            <input
                                type="search"
                                placeholder="Drill, tent, mixer…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid={BROWSE.searchInput}
                                className="w-full rounded-xl border border-[#2e3220]/20 bg-white/70 py-3 pl-10 pr-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </div>
                    </label>

                    {/* Category */}
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Category
                        </span>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            data-testid={BROWSE.categorySelect}
                            className="mt-1 w-full appearance-none rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        >
                            <option value="all">All categories</option>
                            {categories.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    {/* Price range */}
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Price / day
                        </span>
                        <div className="mt-1 flex items-center gap-2">
                            <input
                                type="number"
                                inputMode="numeric"
                                placeholder="Min"
                                min={0}
                                value={minPrice}
                                onChange={(e) => setMinPrice(e.target.value)}
                                data-testid={BROWSE.minPriceInput}
                                className="w-20 rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            <span className="text-xs text-[#5a6142]">to</span>
                            <input
                                type="number"
                                inputMode="numeric"
                                placeholder="Max"
                                min={0}
                                value={maxPrice}
                                onChange={(e) => setMaxPrice(e.target.value)}
                                data-testid={BROWSE.maxPriceInput}
                                className="w-20 rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </div>
                    </div>

                    {/* Availability + clear */}
                    <div className="flex items-end gap-3 md:flex-col md:items-stretch">
                        <button
                            type="button"
                            onClick={() => setOnlyAvailable((v) => !v)}
                            data-testid={BROWSE.availabilityToggle}
                            aria-pressed={onlyAvailable}
                            className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                                onlyAvailable
                                    ? "bg-[#737a3d] text-[#F4F1EA] hover:bg-[#8a9450]"
                                    : "border border-[#2e3220]/20 bg-white/70 text-[#2e3220] hover:bg-[#E8E2D0]"
                            }`}
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            {onlyAvailable ? "Available only" : "Show all"}
                        </button>
                        {hasFilters && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                data-testid={BROWSE.clearFilters}
                                className="inline-flex items-center justify-center gap-1 rounded-xl px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#5a6142] hover:text-[#2e3220]"
                            >
                                <X className="h-3.5 w-3.5" />
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Results count */}
            <div className="mt-8 flex items-center justify-between">
                <p
                    data-testid={BROWSE.resultsCount}
                    className="text-sm font-medium text-[#5a6142]"
                >
                    {loading
                        ? "Searching the block…"
                        : `${items.length} ${items.length === 1 ? "item" : "items"} on Lendly`}
                </p>
            </div>

            {/* Grid */}
            {loading ? (
                <div
                    data-testid={BROWSE.loading}
                    className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div
                            key={i}
                            className="aspect-[4/3.4] animate-pulse rounded-3xl bg-[#E8E2D0]"
                        />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div
                    data-testid={BROWSE.emptyState}
                    className="mt-10 rounded-3xl border border-dashed border-[#2e3220]/20 bg-[#F4F1EA] px-6 py-16 text-center"
                >
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                        <PackageSearch className="h-6 w-6" />
                    </div>
                    <h2 className="font-display text-2xl font-bold text-[#2e3220]">
                        Nothing matches that yet
                    </h2>
                    <p className="mx-auto mt-2 max-w-sm text-sm text-[#5a6142]">
                        Try a different search or clear your filters — your block may not
                        have this one listed quite yet.
                    </p>
                    {hasFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            data-testid={`${BROWSE.clearFilters}-empty`}
                            className="btn-secondary mt-6"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <div
                    data-testid={BROWSE.grid}
                    className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                    {items.map((item) => (
                        <ItemCard key={item.id} item={item} onView={openItem} />
                    ))}
                </div>
            )}

            <ItemDetailModal
                item={activeItem}
                open={modalOpen}
                onOpenChange={(v) => {
                    setModalOpen(v);
                    if (!v) setActiveItem(null);
                }}
            />
        </main>
    );
};

export default Browse;
